import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Loader2, CheckCircle2, Users, Briefcase, ShieldCheck, CalendarDays, Zap, Car, Timer } from "lucide-react";
import { COMPANY } from "../lib/data";
import { loadMetaPixel, loadTikTokPixel, trackLead, specialDeadlineMs, fmtCountdown } from "../lib/weeklySpecialTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#F2B705";
const FLEET_FALLBACK_IMG = "https://static.prod-images.emergentagent.com/jobs/e91af2a1-1cd1-4d88-8ba8-233bf50929e1/images/3d291f0ac8fc03108e7602c3dc8042d4550191dee7b379d65ac6a66e2be7d20e.png";

export default function WeeklySpecial() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ name: "", phone: "", email: "", preferred_pickup_date: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);
  const startedRef = useRef(false);
  const navigate = useNavigate();
  const ref = new URLSearchParams(window.location.search).get("src")
    || new URLSearchParams(window.location.search).get("utm_source") || "";

  useEffect(() => {
    axios.get(`${API}/weekly-special`).then((r) => {
      setData(r.data);
      if (r.data.meta_pixel_id) loadMetaPixel(r.data.meta_pixel_id);
      if (r.data.tiktok_pixel_id) loadTikTokPixel(r.data.tiktok_pixel_id);
    }).catch(() => setData({ weekly_price: 375, featured: null }));
  }, []);

  const v = data?.featured;
  const price = data?.weekly_price ?? 375;
  const promoName = data?.promo_name || "";
  const heroImg = data?.promo_image || v?.image || FLEET_FALLBACK_IMG;
  const set = (k, val) => setF((p) => ({ ...p, [k]: val }));
  const trackStart = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      try { window.fbq && window.fbq("track", "InitiateCheckout"); } catch { /* noop */ }
      try { window.ttq && window.ttq.track && window.ttq.track("InitiateCheckout"); } catch { /* noop */ }
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.email.trim()) { toast.error("Please fill in your name, phone and email."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/weekly-special/lead`, { ...f, ref, waitlist: !!data?.closed });
      trackLead();
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please call us instead.");
    } finally { setBusy(false); }
  };

  const continueUrl = () => {
    const p = new URLSearchParams();
    if (v?.name) p.set("vehicle", v.name);
    if (f.name) p.set("name", f.name);
    if (f.email) p.set("email", f.email);
    if (f.phone) p.set("phone", f.phone);
    if (f.preferred_pickup_date) p.set("pickup_date", f.preferred_pickup_date);
    return `/request?${p.toString()}`;
  };

  const inp = "mt-1.5 w-full bg-white/[0.04] border border-white/15 focus:border-[#F2B705] rounded-lg outline-none px-4 py-3.5 text-white text-base placeholder:text-white/25 transition-colors";
  const lab = "text-[11px] text-white/50 uppercase tracking-[0.15em] font-semibold block";

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="weekly-special-confirmation">
        <TopBar />
        <div className="flex-1 flex items-center justify-center px-5 py-14">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="text-6xl">🎉</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Thanks! Your request has been received.</h1>
            <p className="text-white/60 text-base leading-relaxed">One of our team members will text or call you shortly to confirm availability.</p>
            <div className="bg-white/[0.04] border border-[#F2B705]/30 rounded-xl p-5 space-y-4">
              <p className="text-white/80 font-semibold">Want to speed things up?</p>
              <button data-testid="ws-continue-reservation-btn" onClick={() => navigate(continueUrl())}
                className="w-full py-4 rounded-full bg-[#F2B705] text-black font-bold text-base active:scale-[0.98] transition-transform">
                ✅ Continue My Reservation
              </button>
              <Link data-testid="ws-browse-vehicles-btn" to="/#fleet"
                className="block w-full py-4 rounded-full border border-white/25 text-white font-bold text-base active:scale-[0.98] transition-transform">
                🚗 Browse Available Vehicles
              </Link>
            </div>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 text-[#F2B705] font-semibold text-sm">
              <Phone size={15} /> Or call us now: {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="weekly-special-page">
      <TopBar />

      {/* HERO */}
      <section className="relative">
        <div className="relative h-[46vh] min-h-[300px] sm:h-[54vh] overflow-hidden">
          <img src={heroImg} alt={v ? `${v.year} ${v.name}` : "AutoHire STL fleet"} data-testid="ws-hero-image"
            className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-black/30" />
          {v && (
            <div className="absolute top-4 left-4">
              {v.available_now ? (
                <span data-testid="ws-availability-badge" className="inline-flex items-center gap-1.5 bg-emerald-500/90 text-black text-xs font-bold px-3 py-1.5 rounded-full">
                  <Zap size={12} /> AVAILABLE NOW
                </span>
              ) : (
                <span data-testid="ws-availability-badge" className="inline-flex items-center gap-1.5 bg-white/90 text-black text-xs font-bold px-3 py-1.5 rounded-full">
                  <CalendarDays size={12} /> {v.next_available ? `AVAILABLE ${v.next_available}` : "CURRENTLY RENTED"}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="px-5 -mt-24 relative z-10 max-w-lg mx-auto sm:max-w-2xl">
          <p className="text-[#F2B705] text-xs font-bold tracking-[0.3em] uppercase mb-2">⭐ {promoName || "This Week's Special"}</p>
          <h1 data-testid="ws-vehicle-title" className="font-display text-4xl sm:text-5xl font-extrabold leading-tight">
            {v ? `${v.year} ${v.name}` : "Ride With Us"}
          </h1>
          {v?.color && <p className="text-white/50 text-sm mt-1">{v.color}{v.seats ? ` · ${v.seats} seats` : ""}{v.bags ? ` · ${v.bags} bags` : ""}</p>}
          {!v && <p className="text-white/60 text-base mt-1">Clean, reliable rentals across the St. Louis metro.</p>}

          <div className="mt-5 flex items-end gap-3">
            <div data-testid="ws-weekly-price" className="text-[#F2B705] font-display font-extrabold text-6xl sm:text-7xl leading-none">
              ${Math.round(price)}
            </div>
            <div className="pb-1.5">
              <div className="text-white font-bold text-lg leading-tight">{v ? "/ week" : "/ week & up"}</div>
              {data?.weekly_savings > 0 && <div className="text-emerald-400 text-xs font-semibold">Save ${Math.round(data.weekly_savings)} vs. daily rates</div>}
            </div>
          </div>
          {!v && <p className="text-white/70 text-sm mt-2 font-semibold">Vehicles starting at ${Math.round(price)}/week</p>}
          {data?.promo_description && <p data-testid="ws-promo-description" className="text-white/65 text-sm mt-3 leading-relaxed">{data.promo_description}</p>}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
            {data?.promo_start_date && data?.promo_end_date && <span data-testid="ws-promo-dates">🗓 {data.promo_start_date} → {data.promo_end_date}</span>}
            {data?.promo_deposit > 0 && <span data-testid="ws-promo-deposit">💳 ${Math.round(data.promo_deposit)} deposit</span>}
            {data?.max_bookings > 0 && data?.slots_left !== null && (
              <span data-testid="ws-slots-left" className={data.slots_left <= 2 ? "text-red-400 font-bold" : "text-emerald-300 font-semibold"}>
                🔥 Only {data.slots_left} spot{data.slots_left === 1 ? "" : "s"} left
              </span>
            )}
          </div>

          {data?.countdown_enabled !== false && !data?.closed && <Countdown expiresAt={data?.expires_at} />}

          <button data-testid="ws-reserve-now-btn" onClick={scrollToForm}
            className="mt-6 w-full sm:w-auto sm:px-14 py-4 rounded-full bg-[#F2B705] text-black font-extrabold text-lg shadow-[0_0_40px_rgba(242,183,5,0.35)] active:scale-[0.98] transition-transform">
            Reserve This Special →
          </button>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="px-5 mt-10 max-w-lg mx-auto sm:max-w-2xl">
        <div className="grid grid-cols-3 gap-2 text-center">
          {[[ShieldCheck, "No Airport-Counter Games"], [Users, "Local STL Company"], [Briefcase, "Rideshare & Gig Friendly"]].map(([Icon, t]) => (
            <div key={t} className="bg-white/[0.03] border border-white/10 rounded-xl px-2 py-4">
              <Icon size={18} className="mx-auto text-[#F2B705] mb-1.5" />
              <p className="text-white/70 text-[11px] font-semibold leading-tight">{t}</p>
            </div>
          ))}
        </div>
        {v?.blurb && <p className="text-white/45 text-sm text-center mt-5 italic">&ldquo;{v.blurb}&rdquo;</p>}
      </section>

      {/* LEAD FORM / CLOSED STATE */}
      <section ref={formRef} className="px-5 py-12 max-w-lg mx-auto sm:max-w-xl scroll-mt-6" id="reserve">
        <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-2xl p-6 sm:p-8">
          {data?.closed ? (
            <div data-testid="ws-closed-notice">
              <h2 className="font-display text-lg font-extrabold text-white">
                {data.closed_reason === "sold_out" ? "🔥 This Special Is Fully Booked" : "⏰ This Promotion Has Ended"}
              </h2>
              <p className="text-white/55 text-sm mt-2 leading-relaxed">
                {data.closed_reason === "sold_out"
                  ? "All spots for this promotion have been claimed."
                  : "This special is no longer available."} Join the waiting list below — if a spot opens or a new special drops, you'll be the first to know. Or browse our regular vehicles anytime.
              </p>
              <Link data-testid="ws-view-regular" to="/#fleet" className="mt-4 block w-full py-3.5 rounded-full border border-[#F2B705]/60 text-[#F2B705] font-bold text-center text-sm">🚗 View Regular Vehicles</Link>
              <p className="text-white/40 text-xs uppercase tracking-widest font-bold mt-6 mb-3">Join the waiting list</p>
            </div>
          ) : (
            <>
              <h2 className="font-display text-lg font-extrabold text-white">Lock In {promoName || "This Week's Special"}</h2>
              <p className="text-white/50 text-sm mt-1 mb-6">Takes 30 seconds — we&apos;ll text you to confirm availability.</p>
            </>
          )}
          <form onSubmit={submit} className="space-y-4">
            <label className={lab}>Full Name *
              <input data-testid="ws-form-name" className={inp} value={f.name} onFocus={trackStart} onChange={(e) => set("name", e.target.value)} placeholder="Your name" autoComplete="name" />
            </label>
            <label className={lab}>Phone *
              <input data-testid="ws-form-phone" type="tel" className={inp} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(314) 555-0123" autoComplete="tel" />
            </label>
            <label className={lab}>Email *
              <input data-testid="ws-form-email" type="email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" autoComplete="email" />
            </label>
            <label className={lab}>Preferred Pickup Date <span className="text-white/25 normal-case">(optional)</span>
              <input data-testid="ws-form-pickup-date" type="date" min={new Date().toISOString().slice(0, 10)} className={inp}
                value={f.preferred_pickup_date} onChange={(e) => set("preferred_pickup_date", e.target.value)} />
            </label>
            <button data-testid="ws-form-submit-btn" type="submit" disabled={busy}
              className="w-full py-4 rounded-full bg-[#F2B705] text-black font-extrabold text-base disabled:opacity-60 active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : <><CheckCircle2 size={17} /> {data?.closed ? "Join the Waiting List" : "Reserve This Special"}</>}
            </button>
            {data?.promo_terms && (
              <details className="text-white/40 text-[11px] leading-relaxed" data-testid="ws-promo-terms">
                <summary className="cursor-pointer text-white/50 font-semibold">Promotion terms &amp; conditions</summary>
                <p className="mt-1.5 whitespace-pre-line">{data.promo_terms}</p>
              </details>
            )}
            <p className="text-white/30 text-[11px] text-center leading-relaxed">By submitting you agree we may contact you by call, text or email about this offer. No spam — ever.</p>
          </form>
        </div>

        <div className="text-center mt-8 space-y-2">
          <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 text-[#F2B705] font-bold">
            <Phone size={16} /> {COMPANY.phone}
          </a>
          <p className="text-white/30 text-xs">{COMPANY.name} · {COMPANY.address} · {COMPANY.est}</p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/40 text-xs underline underline-offset-4"><Car size={12} /> Visit our full site</Link>
        </div>
      </section>
    </div>
  );
}

const Countdown = ({ expiresAt }) => {
  const deadline = () => {
    if (expiresAt) {
      const d = new Date(expiresAt.length <= 10 ? `${expiresAt}T23:59:59` : expiresAt);
      return Math.max(0, d.getTime() - Date.now());
    }
    return specialDeadlineMs();
  };
  const [ms, setMs] = useState(deadline());
  useEffect(() => {
    const t = setInterval(() => setMs(deadline()), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [expiresAt]);
  return (
    <div data-testid="ws-countdown" className="mt-4 inline-flex items-center gap-2 bg-red-500/15 border border-red-500/40 rounded-full px-4 py-2">
      <Timer size={15} className="text-red-400" />
      <span className="text-white/80 text-sm font-semibold">{expiresAt ? "This special ends in" : "This special ends Sunday —"}</span>
      <span className="text-red-400 font-mono font-bold text-sm tabular-nums">{fmtCountdown(ms)}</span>
    </div>
  );
};

const TopBar = () => (
  <header className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20">
    <Link to="/" className="flex items-center gap-2.5">
      <img src={COMPANY.logo} alt={COMPANY.name} className="h-9 w-9 object-contain" />
      <span className="font-display font-extrabold text-white tracking-wide text-sm">AUTO-HIRE <span className="text-[#F2B705]">STL</span></span>
    </Link>
    <a data-testid="ws-call-btn" href={COMPANY.phoneHref}
      className="flex items-center gap-1.5 bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-full">
      <Phone size={13} /> Call Now
    </a>
  </header>
);
